/**
 * SignupView — Freemium registration: email, password, first name.
 * Mandatory email verification after signup.
 */

import type { AweniContext } from '../../../context/AweniContext';
import { createAuthLayout } from '../components/AuthLayout';
import { createTurnstile, type TurnstileInstance } from '../components/TurnstileWidget';
import { createPasswordStrength } from '../components/PasswordStrength';
import { validateEmail, validatePassword, validateName } from '../validators';
import { signup } from '../AuthService';

export async function mountSignupView(
  container: HTMLElement,
  ctx: AweniContext,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): Promise<{ unmount: () => void }> {
  const t = ctx.i18n.t.bind(ctx.i18n);
  const layout = createAuthLayout(ctx);
  layout.setTitle(t('auth_signup_title'));
  layout.setSubtitle(t('auth_signup_subtitle'));

  let turnstile: TurnstileInstance | null = null;
  let loading = false;

  const form = document.createElement('form');
  form.setAttribute('novalidate', '');
  form.autocomplete = 'on';

  // First name
  const nameField = _createField('first_name', t('auth_first_name_label'), 'text', 'given-name');
  form.appendChild(nameField.wrapper);

  // Email
  const emailField = _createField('email', t('auth_email_label'), 'email', 'email');
  form.appendChild(emailField.wrapper);

  // Password with strength meter
  const pwField = _createPasswordFieldWithStrength(t('auth_password_label'), 'new-password', t);
  form.appendChild(pwField.wrapper);

  // Terms agreement
  const termsRow = document.createElement('div');
  termsRow.style.cssText = 'display:flex;align-items:flex-start;gap:10px;margin:12px 0 16px;';
  const termsCheck = document.createElement('input');
  termsCheck.type = 'checkbox';
  termsCheck.id = 'auth-terms';
  termsCheck.style.cssText = 'margin-top:3px;min-width:18px;min-height:18px;accent-color:#0A0A0A;';
  const termsLabel = document.createElement('label');
  termsLabel.setAttribute('for', 'auth-terms');
  termsLabel.style.cssText = 'font-size:0.8rem;color:var(--text-secondary,#94A3B8);line-height:1.4;cursor:pointer;';
  termsLabel.textContent = t('auth_terms_agreement');
  termsRow.append(termsCheck, termsLabel);
  form.appendChild(termsRow);

  // Turnstile
  turnstile = await createTurnstile(ctx.theme.isDark);
  form.appendChild(turnstile.container);

  // Submit button
  const submitBtn = document.createElement('button');
  submitBtn.type = 'submit';
  submitBtn.className = 'auth-btn auth-btn-primary';
  submitBtn.innerHTML = `<span>${t('auth_signup_button')}</span>`;
  form.appendChild(submitBtn);

  // Login link
  const loginRow = document.createElement('div');
  loginRow.className = 'auth-footer-row';
  loginRow.innerHTML = `${t('auth_have_account')} `;
  const loginLink = document.createElement('button');
  loginLink.type = 'button';
  loginLink.className = 'auth-link';
  loginLink.textContent = t('auth_login_link');
  loginLink.addEventListener('click', () => opts.onNavigate('login'));
  loginRow.appendChild(loginLink);
  form.appendChild(loginRow);

  // -- Submit handler --
  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (loading) return;

    layout.clearMessages();

    const firstName = nameField.input.value.trim();
    const email = emailField.input.value.trim();
    const password = pwField.input.value;

    // Validate
    const nameResult = validateName(firstName);
    if (!nameResult.valid) {
      layout.showError(t(nameResult.errors[0]));
      nameField.input.focus();
      return;
    }

    const emailResult = validateEmail(email);
    if (!emailResult.valid) {
      layout.showError(t(emailResult.errors[0]));
      emailField.input.focus();
      return;
    }

    const pwResult = validatePassword(password);
    if (!pwResult.valid) {
      layout.showError(t(pwResult.errors[0]));
      pwField.input.focus();
      return;
    }
    if (pwResult.strength < 2) {
      layout.showError(t('auth_error_password_too_weak'));
      pwField.input.focus();
      return;
    }

    if (!termsCheck.checked) {
      layout.showError(t('auth_error_terms_required'));
      return;
    }

    loading = true;
    submitBtn.disabled = true;
    submitBtn.innerHTML = `<span class="auth-spinner"></span>`;

    const result = await signup(email, password, firstName, '', turnstile?.getToken() || undefined);

    if (result.ok) {
      // Show "check your email" screen
      _showCheckEmail(layout, t, email, opts);
    } else {
      const err = result.error;
      let msg = t(`auth_error_${err.code?.toLowerCase() || 'unknown'}`);
      if (msg.startsWith('auth_error_')) msg = err.error;
      layout.showError(msg);
      turnstile?.reset();
    }

    loading = false;
    submitBtn.disabled = false;
    submitBtn.innerHTML = `<span>${t('auth_signup_button')}</span>`;
  });

  layout.card.appendChild(form);
  container.innerHTML = '';
  container.appendChild(layout.wrapper);

  return {
    unmount() {
      turnstile?.destroy();
      container.innerHTML = '';
    },
  };
}

function _showCheckEmail(
  layout: ReturnType<typeof createAuthLayout>,
  t: (key: string) => string,
  email: string,
  opts: { onNavigate: (view: string, params?: Record<string, string>) => void },
): void {
  layout.setTitle(t('auth_verify_email_title'));
  layout.setSubtitle('');
  layout.clearMessages();

  layout.card.innerHTML = '';
  const checkEl = document.createElement('div');
  checkEl.className = 'auth-check-email';

  checkEl.innerHTML = `
    <div class="auth-check-email-icon">
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
        <polyline points="22,6 12,13 2,6"/>
      </svg>
    </div>
    <h2>${t('auth_check_email_heading')}</h2>
    <p>${t('auth_check_email_message').replace('{email}', `<strong>${_escapeHtml(email)}</strong>`)}</p>
  `;

  // Resend button
  const resendBtn = document.createElement('button');
  resendBtn.className = 'auth-btn auth-btn-secondary';
  resendBtn.textContent = t('auth_resend_verification');
  resendBtn.style.marginBottom = '12px';
  resendBtn.addEventListener('click', async () => {
    resendBtn.disabled = true;
    resendBtn.textContent = t('auth_verification_sent');
    const { resendVerification } = await import('../AuthService');
    await resendVerification(email);
    setTimeout(() => {
      resendBtn.disabled = false;
      resendBtn.textContent = t('auth_resend_verification');
    }, 30000);
  });
  checkEl.appendChild(resendBtn);

  // Back to login
  const loginBtn = document.createElement('button');
  loginBtn.className = 'auth-btn auth-btn-primary';
  loginBtn.textContent = t('auth_go_to_login');
  loginBtn.addEventListener('click', () => opts.onNavigate('login'));
  checkEl.appendChild(loginBtn);

  layout.card.appendChild(checkEl);
}

function _escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function _createField(
  name: string,
  label: string,
  type: string,
  autocomplete: string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';

  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', `auth-${name}`);

  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = type;
  input.id = `auth-${name}`;
  input.name = name;
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);

  wrapper.append(labelEl, input);
  return { wrapper, input };
}

function _createPasswordFieldWithStrength(
  label: string,
  autocomplete: string,
  t: (key: string) => string,
): { wrapper: HTMLElement; input: HTMLInputElement } {
  const wrapper = document.createElement('div');
  wrapper.className = 'auth-field';

  const labelEl = document.createElement('label');
  labelEl.className = 'auth-label';
  labelEl.textContent = label;
  labelEl.setAttribute('for', 'auth-signup-password');

  const inputWrap = document.createElement('div');
  inputWrap.className = 'auth-input-wrap';

  const input = document.createElement('input');
  input.className = 'auth-input';
  input.type = 'password';
  input.id = 'auth-signup-password';
  input.name = 'password';
  input.autocomplete = autocomplete as AutoFill;
  input.setAttribute('aria-label', label);

  const toggleBtn = document.createElement('button');
  toggleBtn.type = 'button';
  toggleBtn.className = 'auth-toggle-pw';
  toggleBtn.setAttribute('aria-label', 'Toggle password visibility');
  toggleBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
  toggleBtn.addEventListener('click', () => {
    const show = input.type === 'password';
    input.type = show ? 'text' : 'password';
    toggleBtn.innerHTML = show
      ? '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>'
      : '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';
  });

  inputWrap.append(input, toggleBtn);

  // Password strength meter
  const strength = createPasswordStrength(t);
  input.addEventListener('input', () => strength.update(input.value));

  wrapper.append(labelEl, inputWrap, strength.container);
  return { wrapper, input };
}
